import { AppError, AppErrorOptions } from "./display/AppError.js";
import { Blog } from "./display/Blog.js";
import { Datepicker } from "./display/Datepicker.js";
import { Gallery, GalleryItemData } from "./display/Gallery.js";
import { Headline } from "./display/Headline.js";
import { Toolbar } from "./display/Toolbar.js";
import { Unsupported } from "./display/Unsupported.js";
import { Canvas3d } from "./display3d/Canvas3d.js";
import { Globe3d } from "./display3d/Globe3d.js";
import { Grid3d } from "./display3d/Grid3d.js";
import { HeightMap } from "./display3d/HeightMap.js";
import { OrbitCamera3d } from "./display3d/OrbitCamera3d.js";
import { Rings3d } from "./display3d/Rings3d.js";
import { Route3d } from "./display3d/Route3d.js";
import { DataManager } from "./model/DataManager.js";
import { VOBlog } from "./model/DataStructure.js";
import { DateUtil } from "./model/DateUtil.js";

/**
 * Test
 */
class Expedition {

    private _data: DataManager;
    private _headline: Headline;
    private _globe: Globe3d;
    private _blog: Blog;
    private _datepicker: Datepicker;
    private _route: Route3d = null;
    private _canvas: Canvas3d;
    private _orbitCamera: OrbitCamera3d;
    private _toolbar: Toolbar;
    private _gallery: Gallery;

    constructor(public readonly element: HTMLDivElement) {
        e5.console.log("[eventfive.awi.polarstern-v2]","[Expedition]");

        if (e5.Caps.isInternetExplorer) {
            new Unsupported().create({
                container: this.element
            });
        } else {
            this.init();
        }
    }

    private async wait(time: number): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            setTimeout(() => {
                resolve(true);
            }, time);
        });
    }

    private async init() {

        this.element.innerHTML = `
        <div class="awi-app__content">
            <div class="awi-app__start">
                <div class="awi-app__headline"></div>
                <div class="awi-app__canvas"></div>
                <div class="awi-app__toolbar"></div>
                <div class="awi-app__datepicker"></div>
            </div>
            <div class="awi-app__end">
                <div class="awi-app__blog"></div>
            </div>
        </div>
        <div class="awi-app__preloader">
            <div class="awi-app__preloader-content">
                <div class="awi-app__preloader-icon e5 icon-789"></div>  
                <div class="awi-app__preloader-progress"></div>  
            </div>
        </div>
        `;

        try {
            this._data = new DataManager();
            await this._data.load(this.element.getAttribute("data-dataurl"));
        } catch (error) {
            console.warn(error);
            this.showError({
                message: {
                    de: "Fehler beim Laden der Inhalte",
                    en: "Error loading content"
                },
                container: this.element,
                error: error
            });
            this.hidePreloader();
            return false;
        }

        // await this.wait(2000);
        // return;


        // e5.TouchEmulator.create();

        var userDate: Date = new Date();//this._data.data.expedition.date_begin;
        var blog: VOBlog = null;
        if (this.element.getAttribute("data-blog")) {
            blog = this._data.data.awi_blog.id(parseInt(this.element.getAttribute("data-blog")));
            if (blog) {
                userDate = blog.date;
            }
        }

        var date: Date = DateUtil.clamp(userDate,
            this._data.data.expedition.date_begin,
            this._data.data.expedition.date_end);

        this._toolbar = new Toolbar().create({
            container: this.element.querySelector(".awi-app__toolbar"),
        });

        this._headline = new Headline().create({
            container: this.element.querySelector(".awi-app__headline"),
            assetsUrl: this.element.getAttribute("data-assetsurl"),
            data: this._data,
            date: date
        });

        if (this.isWebGLAvailable()) {
            try {
                await this.set3d(date);
            } catch (error) {
                console.warn(error);
                this.showError({
                    message: {
                        de: "Fehler beim erzeugen der 3D Inhalte",
                        en: "Error creating 3D content"
                    },
                    container: this.element.querySelector(".awi-app__canvas"),
                    error: error
                });
            }
        } else {
            this.showError({
                message: {
                    de: "WEBGL nicht verfügbar",
                    en: "WEBGL not supported"
                },
                container: this.element.querySelector(".awi-app__canvas")
            });
        }

        this._datepicker = new Datepicker().create({
            container: this.element.querySelector(".awi-app__datepicker"),
            date: date,
            maxDate: DateUtil.clamp(
                this._data.data.expedition.date_end,
                this._data.data.expedition.date_begin,
                new Date()),
            minDate: this._data.data.expedition.date_begin
        });

        this._blog = new Blog().create({
            container: this.element.querySelector(".awi-app__blog"),
            data: this._data,
            date: date,
            blog: blog,
            likesServiceUrl: this.element.getAttribute("data-likesurl"),
            assetsUrl: this.element.getAttribute("data-assetsurl"),
        });

        this.createGallery();

        this._blog.onChange.debounce((d) => {
            var time: number | "auto" = 0.3;

            this._datepicker.setDate(d, time, false);
            this._headline.setDate(d);
            if (this._route) {
                var s = this._route.setDate(d, time);
                if (s) {
                    this._orbitCamera.lookAt(s.longitude, s.latitude, time);
                }
            }
        }, 500);

        this._datepicker.onChangeEnd.add((d) => {
            this._blog.setDate(d, "auto", false);
        });

        this._datepicker.onChange.add((d) => {
            this._headline.setDate(d);
            if (this._route) {
                var s = this._route.setDate(d);
                if (s) {
                    this._orbitCamera.lookAt(s.longitude, s.latitude, 2);
                }
            }
        });

        var t: any = 0;
        window.addEventListener("resize", () => {
            this.element.classList.add("awi-app--no-transition");
            clearTimeout(t);
            t = setTimeout(() => {
                this.element.classList.remove("awi-app--no-transition");
            }, 50);
        });

        this.setToolbarItems();

        this.hidePreloader();

        // window.offlineOverlay.enabled = false;

        // document.body.addEventListener("click", () => {
        //     console.log("CLICK");
        //     this.notify();
        // });
    }

    private createGallery(): void {
        this._gallery = new Gallery().create({
            container: document.body
        });
        var mediasDe: GalleryItemData[] = [];
        var mediasEn: GalleryItemData[] = [];
        for (var blog of this._data.data.awi_blog) {

            if (blog.medias && blog.medias.length > 0) {
                for (var m of blog.medias) {
                    var d: GalleryItemData = {
                        id: m.uid + "",
                        width: m.width,
                        height: m.height,
                        url: m.url,
                        type: m.type as any,
                        date: blog.date,
                        poster: m.sizes["large"],
                        author: {
                            de: m.info.author_de,
                            en: m.info.author_en
                        },
                        title: {
                            de: m.info.context_title_de,
                            en: m.info.context_title_en
                        }
                    };

                    if (m.info.language === "all"
                        || m.info.language === "de") {
                        mediasDe.push(d);
                    }

                    if (m.info.language === "all"
                        || m.info.language === "en") {
                        mediasEn.push(d);
                    }
                }
            }
        }

        // var update = () => {
        //     if (lang.current === "de") {
        //         this._gallery.load(mediasDe);
        //     } else {
        //         this._gallery.load(mediasEn);
        //     }
        // }
        // update();
        // lang.onChange.add(update);

        // this._gallery.load(mediasEn);
        // this._gallery.open({index: 0});

        e5.event.on(document.body, "click", "[data-gallery]", (e) => {
            var id = e.target.getAttribute("data-gallery");
            this._gallery.open({
                id: id,
                initiator: e.target as HTMLElement,
                items: lang.current === "de" ? mediasDe : mediasEn
            });
        });
    }

    private hidePreloader(): void {
        var pre: HTMLDivElement = this.element.querySelector(".awi-app__preloader");
        if (!pre || !pre.parentNode) {
            return;
        }

        pre.classList.add("awi-app__preloader--hidden");

        gsap.to(pre, {
            duration: 0.4,
            opacity: 0,
            delay: 0.15,
            onComplete: () => {
                if (pre.parentNode) {
                    pre.remove();
                }
            }
        });
    }

    private showError(options: AppErrorOptions): void {
        options.container.innerHTML = "";
        new AppError().create(options);
        if (options.error && window.errorView) {
            window.errorView.add(options.error, "showerror");
        }
    }

    private isWebGLAvailable() {
        try {
            const canvas = document.createElement('canvas');
            return !!(window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl')));

        } catch (e) {
            return false;
        }
    }

    private async set3d(date: Date): Promise<any> {
        var radius: number = 100;

        this._canvas = new Canvas3d().create({
            container: this.element.querySelector(".awi-app__canvas"),
            radius: radius
        });

        // var hm = new HeightMap({
        //     assetsUrl: this.element.getAttribute("data-assetsurl"),
        //     radius: radius
        // });
        // await hm.create();

        var hm: HeightMap = null;

        this._globe = new Globe3d({
            assetsUrl: this.element.getAttribute("data-assetsurl"),
            canvas: this._canvas,
            radius: radius,
            markerLists: this._data.data.awi_marker,
            heightMap: hm
        });

        var grid: Grid3d = new Grid3d({
            canvas: this._canvas,
            radius: radius + 0.5
        });

        var rings = new Rings3d({
            canvas: this._canvas,
            radius: radius
        });

        this._route = new Route3d({
            date: date,
            canvas: this._canvas,
            data: this._data,
            radius: radius,
            heightMap: hm
        });

        var day = this._data.getDay(date);
        this._orbitCamera = new OrbitCamera3d({
            canvas: this._canvas,
            latitude: (day && day.position) ? day.position.latitude : null,
            longitude: (day && day.position) ? day.position.longitude : null,
            toolbar: this._toolbar,
            radius: radius
        });

        /**
         * Earth Zoom
         */
        var plus = this._toolbar.add({
            group: "orbit",
            icon: "far fa-plus",
            label: {
                de: "Heranzoomen",
                en: "Zoom in"
            }
        });
        var minus = this._toolbar.add({
            group: "orbit",
            icon: "far fa-minus",
            label: {
                de: "Herauszoomen",
                en: "Zoom out"
            }
        });
        var updateControls = () => {
            plus.disabled = this._orbitCamera.zoomTarget >= this._orbitCamera.maxZoom - 0.01;
            minus.disabled = this._orbitCamera.zoomTarget <= this._orbitCamera.minZoom + 0.01;
        };
        updateControls();
        this._orbitCamera.onZoom.add(updateControls);
        plus.element.addEventListener("click", () => this._orbitCamera.zoomIn());
        minus.element.addEventListener("click", () => this._orbitCamera.zoomOut());


        /**
         * Look at ship
         */
        var home = this._toolbar.add({
            group: "home",
            icon: "fal fa-ship",
            label: {
                de: "Polarstern anzeigen",
                en: "Show Polarstern"
            }
        });
        home.element.addEventListener("click", () => {
            var day = this._data.getDay(this._datepicker.date);
            if (day) {
                var sensor = day.position;
                var time = 0.7;
                this._orbitCamera.lookAt(sensor.longitude, sensor.latitude, time);
                var minZoom = 12;
                if (this._orbitCamera.zoom < minZoom) {
                    this._orbitCamera.setZoom(minZoom, time);
                }
            }
        });

        this._globe.onRequestPosition.add((pos) => {
            var time = 0.7;
            this._orbitCamera.lookAt(pos.lng, pos.lat, time);
            if (pos.zoom !== undefined) {
                this._orbitCamera.setZoom(pos.zoom, time);
            }
        });
    }

    /**
     * Toolbar
     */
    private setToolbarItems(): void {

        /**
         * Expand/Collaps (mobile only)
         */
        var expandMobile = this._toolbar.add({
            group: "app-desktop",
            icon: "fal fa-expand",
            iconActive: "fal fa-compress",
            type: "pressed",
            label: {
                de: "Karte maximieren",
                en: "Maximize map"
            },
            labelActive: {
                de: "Karte minimieren",
                en: "Minimize map"
            }
        });
        expandMobile.group.element.classList.add("awi-app__mobile-only");
        expandMobile.element.addEventListener("click", () => {
            this.element.classList.toggle("awi-app--expanded");
            expandMobile.active = this.element.classList.contains("awi-app--expanded");
        });

        /**
         * Fullscreen/Normal (desktop only)
         */
        // if (e5.FullscreenUtil.isSupported) {
        //     var expandDesktop = this._toolbar.add({
        //         group: "app-mobile",
        //         icon: "fal fa-expand",
        //         iconActive: "fal fa-compress"
        //     });
        //     expandDesktop.group.element.classList.add("awi-app__desktop-only");
        //     expandDesktop.element.addEventListener("click", () => {
        //         e5.FullscreenUtil.toggle(document.body);
        //     });
        //     e5.FullscreenUtil.onChange.add((full) => {
        //         expandDesktop.active = full;
        //     });
        // }
    }

    private async notify() {
        e5.console.log(Notification.permission);
        const result = await Notification.requestPermission();
        e5.console.log("RESULT", result);
        if (result === 'granted') {
            const noti = new Notification('Hello!', {
                body: 'It’s me.',
                lang: lang.current
                // icon: 'mario.png',

            });
            // noti.
            noti.onclick = () => alert('clicked');
        }
    }
}

document.addEventListener("DOMContentLoaded", () => {
    var app = document.querySelector<HTMLDivElement>(".awi-app");
    if (app) {
        new Expedition(app);
    }
});
